import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, ArrowLeft, Calendar, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BlogList() {
  const { t } = useLanguage();
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    axios.get(`${API}/blogs`).then(r => setBlogs(r.data)).catch(() => {});
    axios.post(`${API}/analytics/visit`, { page: '/blog' }).catch(() => {});
  }, []);

  return (
    <div data-testid="blog-page">
      <section className="py-24 sm:py-32 bg-secondary/50">
        <div className="container mx-auto px-6 md:px-8 text-center">
          <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">{t('blog.title')}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light">{t('blog.title')}</h1>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="container mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.map((blog, i) => (
              <Card key={blog.id} className="transition-card overflow-hidden rounded-2xl border-border" data-testid={`blog-card-${i}`}>
                <div className="aspect-[16/10] overflow-hidden bg-muted">
                  <img src={blog.image_url} alt={blog.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> {blog.author}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(blog.created_at).toLocaleDateString()}</span>
                  </div>
                  <h3 className="font-heading text-lg font-medium mt-3">{blog.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{blog.excerpt}</p>
                  <Link to={`/blog/${blog.slug}`}>
                    <Button variant="ghost" size="sm" className="mt-4 text-sm p-0 h-auto" data-testid={`blog-read-${i}`}>
                      {t('blog.readMore')} <ArrowRight className="ml-1 w-3.5 h-3.5" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          {blogs.length === 0 && <p className="text-center text-muted-foreground py-16">No articles available yet.</p>}
        </div>
      </section>
    </div>
  );
}

function BlogPost() {
  const { t } = useLanguage();
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blogs/${slug}`).then(r => setBlog(r.data)).catch(() => {}).finally(() => setLoading(false));
    axios.post(`${API}/analytics/visit`, { page: `/blog/${slug}` }).catch(() => {});
  }, [slug]);

  if (loading) return <div className="py-32 text-center text-muted-foreground">Loading...</div>;
  if (!blog) return <div className="py-32 text-center text-muted-foreground">Article not found</div>;

  return (
    <div className="py-16 sm:py-24" data-testid="blog-post-page">
      <div className="container mx-auto px-6 md:px-8 max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors" data-testid="back-to-blog">
          <ArrowLeft className="w-4 h-4" /> {t('blog.title')}
        </Link>
        <div className="rounded-2xl overflow-hidden mb-8">
          <img src={blog.image_url} alt={blog.title} className="w-full h-[300px] md:h-[400px] object-cover" data-testid="blog-post-image" />
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
          <span className="flex items-center gap-1"><User className="w-4 h-4" /> {blog.author}</span>
          <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date(blog.created_at).toLocaleDateString()}</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-light" data-testid="blog-post-title">{blog.title}</h1>
        <div className="mt-8 prose prose-lg max-w-none">
          {blog.content.split('\n').map((p, i) => p.trim() ? <p key={i} className="text-base text-muted-foreground leading-relaxed mb-4">{p}</p> : null)}
        </div>
      </div>
    </div>
  );
}

export default function Blog() {
  const { slug } = useParams();
  return slug ? <BlogPost /> : <BlogList />;
}
